import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Nav from "../components/landing/Nav";
import Footer from "../components/landing/Footer";
import { BLOG_POSTS } from "../data/content";
import { ArrowLeft, ArrowRight } from "lucide-react";

function formatDate(d) {
    try {
        return new Date(d).toLocaleDateString("en-NZ", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        });
    } catch {
        return d;
    }
}

function renderParagraph(text, idx) {
    // ordered list block: starts with "1. "
    if (/^\d+\.\s/.test(text)) {
        return (
            <p
                key={idx}
                className="font-display text-base font-bold uppercase tracking-wider text-coral lg:text-lg"
            >
                {text}
            </p>
        );
    }
    // bold lead-in `**...**` at the start
    const m = text.match(/^\*\*(.+?)\*\*\s*(.*)$/);
    if (m) {
        return (
            <p key={idx} className="text-base leading-relaxed text-ink/85 lg:text-lg">
                <strong className="text-ink">{m[1]}</strong>{" "}
                {m[2]}
            </p>
        );
    }
    return (
        <p key={idx} className="text-base leading-relaxed text-ink/85 lg:text-lg">
            {text}
        </p>
    );
}

export default function BlogPost() {
    const { slug } = useParams();
    const idx = BLOG_POSTS.findIndex((p) => p.slug === slug);
    if (idx === -1) return <Navigate to="/blog" replace />;
    const post = BLOG_POSTS[idx];
    const next = BLOG_POSTS[(idx + 1) % BLOG_POSTS.length];

    return (
        <div className="min-h-screen bg-page text-ink" data-testid={`blog-post-${slug}`}>
            <Nav />
            <main>
                <section className="relative overflow-hidden border-b border-hairline py-16 lg:py-24">
                    <div className="pointer-events-none absolute -top-10 left-1/3 h-80 w-80 animate-blob rounded-full bg-coral/15 blur-3xl" />
                    <div className="relative mx-auto max-w-[820px] px-5 lg:px-10">
                        <Link
                            to="/blog"
                            className="mb-8 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.22em] text-ink/55 hover:text-coral"
                            data-testid="post-back"
                        >
                            <ArrowLeft className="h-3.5 w-3.5" /> All posts
                        </Link>
                        <div className="mb-4 flex flex-wrap items-center gap-3 text-xs font-bold uppercase tracking-[0.22em] text-coral">
                            <span>{post.category}</span>
                            <span className="text-ink/35">·</span>
                            <span className="text-ink/55">{post.readingTime}</span>
                            <span className="text-ink/35">·</span>
                            <span className="text-ink/55">{formatDate(post.date)}</span>
                        </div>
                        <h1 className="font-display text-4xl font-black leading-[0.98] tracking-tight text-ink sm:text-5xl lg:text-6xl">
                            {post.title}
                        </h1>
                        <p className="mt-4 text-sm uppercase tracking-widest text-ink/55">
                            By {post.author}
                        </p>
                    </div>
                </section>

                <section className="border-b border-hairline bg-page py-14 lg:py-20">
                    <article className="mx-auto max-w-[760px] space-y-5 px-5 lg:px-10">
                        {post.body.map((p, i) => renderParagraph(p, i))}
                    </article>
                </section>

                {/* CTA */}
                <section className="border-b border-hairline bg-white py-16 lg:py-20">
                    <div className="mx-auto max-w-[820px] px-5 text-center lg:px-10">
                        <div className="mb-4 text-[11px] font-bold uppercase tracking-[0.22em] text-coral">
                            Want help making it happen?
                        </div>
                        <h3 className="font-display text-3xl font-black uppercase leading-tight tracking-tight text-ink lg:text-4xl">
                            We&apos;ll run the social, you run the shop.
                        </h3>
                        <Link
                            to="/#contact"
                            className="mt-8 inline-flex items-center gap-2 border-2 border-coral bg-coral px-6 py-3.5 font-display text-sm font-bold uppercase tracking-wider text-ink hover:-translate-y-0.5 hover:frame"
                            data-testid="post-cta"
                        >
                            Get the chat going →
                        </Link>
                    </div>
                </section>

                {/* Up next */}
                <section className="bg-page py-16 lg:py-20">
                    <div className="mx-auto max-w-[820px] px-5 lg:px-10">
                        <div className="mb-6 text-[11px] font-bold uppercase tracking-[0.22em] text-coral">
                            // Read next
                        </div>
                        <Link
                            to={`/blog/${next.slug}`}
                            className="group flex items-end justify-between gap-6 border-b-2 border-ink/15 pb-6 hover:border-coral"
                            data-testid="post-up-next"
                        >
                            <div>
                                <div className="text-xs uppercase tracking-widest text-ink/55">
                                    {next.category}
                                </div>
                                <div className="mt-2 font-display text-2xl font-black text-ink lg:text-3xl">
                                    {next.title}
                                </div>
                            </div>
                            <ArrowRight className="h-7 w-7 shrink-0 text-coral transition-transform group-hover:translate-x-1" />
                        </Link>
                    </div>
                </section>
            </main>
            <Footer />
        </div>
    );
}
